import { useTranslation } from 'react-i18next'
import { Radio, Tv2, Youtube } from 'lucide-react'
import type { Investigation } from '@/domains/types'

interface Props {
  investigation: Investigation
}

export default function InvestigationLiveBanner({ investigation }: Props) {
  const { t, i18n } = useTranslation()
  const { liveDate, twitchUrl, youtubeUrl } = investigation

  if (!liveDate) {
    if (!youtubeUrl && !twitchUrl) return null
    return (
      <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2 text-primary">
          <Youtube className="size-5 shrink-0" />
          <p className="text-sm font-semibold">{t('investigation_detail.live_video_label')}</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          {youtubeUrl && (
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-youtube px-4 py-2 text-sm font-medium text-white hover:bg-youtube-dark transition-colors"
            >
              <Youtube className="size-4" />
              {t('investigation_detail.live_watch_video')}
            </a>
          )}
          {twitchUrl && (
            <a
              href={twitchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-twitch px-4 py-2 text-sm font-medium text-white hover:bg-twitch-dark transition-colors"
            >
              <Tv2 className="size-4" />
              {t('investigation_detail.live_watch_twitch')}
            </a>
          )}
        </div>
      </div>
    )
  }

  const liveDateObj = new Date(liveDate)
  const replayThreshold = new Date(liveDateObj.getTime() + 3 * 60 * 60 * 1000)
  const isReplay = new Date() >= replayThreshold

  if (isReplay && !youtubeUrl) return null

  const formattedDate = liveDateObj.toLocaleDateString(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
  const formattedTime = liveDateObj.toLocaleTimeString(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-center gap-2 text-primary">
        <Radio className="size-5 shrink-0" />
        <div>
          <p className="text-sm font-semibold">
            {isReplay
              ? t('investigation_detail.live_replay_label')
              : t('investigation_detail.live_label')}
          </p>
          {!isReplay && (
            <p className="text-sm text-muted-foreground">
              {formattedDate} {t('investigation_detail.live_at')} {formattedTime}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 sm:ml-auto">
        {isReplay ? (
          youtubeUrl && (
            <a
              href={youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              <Youtube className="size-4" />
              {t('investigation_detail.live_watch_replay')}
            </a>
          )
        ) : (
          <>
            {twitchUrl && (
              <a
                href={twitchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-twitch px-4 py-2 text-sm font-medium text-white hover:bg-twitch-dark transition-colors"
              >
                <Tv2 className="size-4" />
                {t('investigation_detail.live_watch_twitch')}
              </a>
            )}
            {youtubeUrl && (
              <a
                href={youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg bg-youtube px-4 py-2 text-sm font-medium text-white hover:bg-youtube-dark transition-colors"
              >
                <Youtube className="size-4" />
                {t('investigation_detail.live_watch_youtube')}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  )
}
